import { Injectable, inject } from "@angular/core";
import { cloneDeep, isNil } from "lodash";
import { Observable } from "rxjs";
import { filter, map, withLatestFrom } from "rxjs/operators";
import { VirkeskopskontraktPrisBerakningsTyper } from "~app/common/models/constants";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { Kontrakt } from "../models/kontrakt-models";
import { IAreaSelection } from "../models/pristabell/marknadsprislistaFilter";
import { MarknadsprislistaFilterInstallningar } from "../models/pristabell/pristabell-filter-installningar";
import { IHandelssortimentSenasteValdPrislista, UtskriftInstallningar } from "../models/utskrift/utskrift-installningar";
import { KontraktUtskriftValues } from "../models/utskrift/utskriftValues-models";
import { KontraktMediatorService } from "./kontrakt-mediator.service";

@Injectable()
export class UtskriftWebinstallningarService {
    readonly resultatomradesFilter$: Observable<IAreaSelection[]>;
    readonly senastValdPrislista$: Observable<IHandelssortimentSenasteValdPrislista[]>;

    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly webinstallningarService = inject(WebinstallningarService);

    constructor() {
        this.senastValdPrislista$ = this.webinstallningarService.listenKeyAsync<UtskriftInstallningar>(UtskriftInstallningar.key).pipe(
            filter(installningar => !isNil(installningar)),
            map(installningar => installningar.handelssortimentSenasteValdaPrislistor),
            withLatestFrom(this.kontraktMediatorService.kontrakt$),
            map(([
                prislista,
                kontrakt
            ]) => this.filterAndSortPrislista(prislista, kontrakt)));

        const filterInstallningar = this.webinstallningarService
            .listenKeyAsync<MarknadsprislistaFilterInstallningar>(MarknadsprislistaFilterInstallningar.key);
        this.resultatomradesFilter$ = filterInstallningar.pipe(
            map(installningar => installningar?.omradesFilter ?? [])
        );
    }

    filterAndSortPrislista(prislista: IHandelssortimentSenasteValdPrislista[], kontrakt: Kontrakt): IHandelssortimentSenasteValdPrislista[] {
        return prislista
            .map(p => ({
                datum: new Date(p.datum),
                prislista: p
            }))
            .sort((a, b) => a.datum.getTime() - b.datum.getTime())
            .map(x => x.prislista);
    }

    mergeUniqueHandelssortimentSenasteValdaPrislistor(existingRows: IHandelssortimentSenasteValdPrislista[], newRows: IHandelssortimentSenasteValdPrislista[]) {
        const filteredExistingRows = existingRows.filter(b => {
            // Filtrera bort rader från existingRows som finns i newRows på vlg + handelssortimentId
            // eslint-disable-next-line sonarjs/prefer-single-boolean-return
            if (!newRows.some(a => (b !== undefined && a.vlg === b.vlg &&
                ((a.handelssortimentId === b.handelssortimentId && a.handelssortimentId) ||
                    (a.handelssortimentsgruppId === b.handelssortimentsgruppId && !a.handelssortimentId && !b.handelssortimentId && a.handelssortimentsgruppId))))) {
                return true;
            }
            return false;
        });
        return filteredExistingRows.concat(newRows);
    }

    mergeUtskriftInstallningar(model: KontraktUtskriftValues) {
        if (ObjectUtils.isNullOrUndefined(model?.pristabell)) {
            return;
        }
        const handelssortimentSenasteValdaPrislistor = model.pristabell
            .filter(x => x.prisberakning === VirkeskopskontraktPrisBerakningsTyper.Prislista && x.marknadsprislistaId)
            .map(rad => ({
                handelssortimentId: rad.handelssortiment_Id,
                handelssortimentsgruppId: rad.handelssortimentsgrupp_Id,
                vlg: rad.marknadsprislistaVidLeveransGallande ? true : false,
                marknadsprislistaId: rad.marknadsprislistaId,
                viol3: rad.marknadsprislistaViol3,
                datum: new Date().toString()
            }));

        const currentInstallningar = this.webinstallningarService.get<UtskriftInstallningar>(UtskriftInstallningar.key)?.handelssortimentSenasteValdaPrislistor || [];

        const merged = this.mergeUniqueHandelssortimentSenasteValdaPrislistor(
            currentInstallningar,
            handelssortimentSenasteValdaPrislistor
        );

        merged.sort((a, b) => a.handelssortimentId - b.handelssortimentId);

        const installning = new UtskriftInstallningar();
        installning.handelssortimentSenasteValdaPrislistor = merged;
        this.webinstallningarService.setLocal<UtskriftInstallningar>(UtskriftInstallningar.key, installning);
    }

    saveResultatomradesFilter(resultatomradesFilter: IAreaSelection[]) {
        const installning = new MarknadsprislistaFilterInstallningar();
        installning.omradesFilter = cloneDeep(resultatomradesFilter);
        this.webinstallningarService.set<MarknadsprislistaFilterInstallningar>(
            MarknadsprislistaFilterInstallningar.key, installning);
    }

    saveUtskriftInstallningar() {
        const installning = this.webinstallningarService.get<UtskriftInstallningar>(UtskriftInstallningar.key);
        this.webinstallningarService.set<UtskriftInstallningar>(UtskriftInstallningar.key, installning);
    }
}
